import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { secureGet, secureDelete, securePost } from "../../config/api";
import { useHistory } from "react-router-dom";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Filter from "../../components/Filter";
import Rows from "../../components/Rows";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";
import { PrimaryButton } from "../../components/PrimaryButton";
import { ReactComponent as CheckIcon } from "../../icons/checkG.svg";

import DeleteIcon from "../../icons/trashR.svg";
import modifyIcon from "../../icons/edit.svg";
import AddIcon from "../../icons/plusB.svg"

const STATE_COLORS = {
  non_attached: "#EF476F",
  part_attached: "#F8B916",
  attached: "#06D6A0",
};

const STATUS_COLORS = {
  CREATED: "#0283F4",
  UNDER_APPROVAL: "#FEA26F",
  VALIDATED: "#97DFFC",
  DELIVERED: "#06D6A0",
  DECLINED: "#EF466F",
  CANCELLED: "#A8ADC3",
};

const Payments = () => {
  const [, setPaymentsData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    paymentStatus: null,
    needle: "",
    dateRangeEcheance: undefined,
    nature: null,
    treated: null,
  });
  const [clients, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([])
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [requestBody, setRequestBody] = useState(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCommercial = role === "ROLE_COMMERCIAL";

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const determineState = (totalAttachedAmount, amount) => {
    if (totalAttachedAmount === 0) return "non_attached";
    if (totalAttachedAmount < amount) return "part_attached";
    if (totalAttachedAmount === amount || totalAttachedAmount > amount) return "attached";
    return "unknown";
  };

  const TREATED_COLORS = {
    true: "#06D6A0",
    false: "#ADAFC6",
  };

  const getTreatedLabel = (state) => t(`treated_status.${state}`);

  const columns = [
    {
      label: t(`columns.paymentNumber`),
      key: "reference",
      render: (reference, row) => (
        <div className="flex flex-col space-y-1">
          <span className="text-[16px] font-semibold text-[#38485C]">
            {reference}
          </span>
          {row.status === "UNDER_APPROVAL" && (
            <span
              className="text-white text-[12px] font-semibold px-1 py-0 rounded-full w-fit"
              style={{ backgroundColor: STATUS_COLORS[row.status] || "#A8ADC3" }}
            >
              {t(`payment_status.${row.status}`)}
            </span>
          )}
        </div>
      )
    }
    , {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Client", key: "client", render: (client) => client?.name || "N/A" },
    { label: t(`columns.by`), key: "createdBy", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: t(`columns.paymentType`), key: "type", render: (type) => type.name },
    { label: t(`columns.montant`), key: "amount" },
    {
      label: t("columns.status"),
      key: "state",
      render: (_, payment) => {
        const statusKey = determineState(payment.totalAttachedAmount, payment.amount);
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
            style={{ backgroundColor: STATE_COLORS[statusKey] || "#A8ADC3" }}
          >
            {t(`payment_attach_status.${statusKey}`).toUpperCase()}
          </span>
        );
      },
    },
    {
      label: t("columns.state"), key: "treated",
      render: (treated) => {
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
            style={{ backgroundColor: TREATED_COLORS[treated] || "#A8ADC3" }}
          >
            {getTreatedLabel(treated).toUpperCase()}
          </span>
        );
      },
    },
    {
      label: formatAmount(paymentsSummary),
      key: "actionss",
      render: (_, item) => (
        <div className="flex items-center justify-end  gap-x-4">
          {role === 'ROLE_ADMIN' && item.status === "UNDER_APPROVAL" && (
            <button onClick={(e) => {
              e.stopPropagation();
              handleValidate(item);
            }}
              className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
            >
              <CheckIcon className="w-5 h-5" strokeWidth={3} />
            </button>
          )}
          {role === 'ROLE_AGENT' && item.treated === false && item.status !== "UNDER_APPROVAL" &&(
            <button onClick={(e) => {
              e.stopPropagation();
              handleTreat(item);
            }}
              className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] "
            >
              <CheckIcon className="w-5 h-5" strokeWidth={3} />
            </button>
          )}

          <button onClick={(e) => {
            e.stopPropagation();
            handleModify(item);
          }}
            className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] "
          >
            <img src={modifyIcon} alt="modify" className="w-5 h-5" />
          </button>

          <button onClick={(e) => {
            e.stopPropagation();
            handleDelete(item);
          }}
            className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] "
          >
            <img src={DeleteIcon} alt="delete" className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  const handleValidate = async (payment) => {
    try {
      const response = await securePost(`/payments/updateValidated`, { paymentId: payment.id }, showSnackbar);
      fetchPaginatedPayments();
      showSnackbar(t(`confirmation.paymentValidation`));
    }
    catch (error) {
      console.error("Failed to update payment status:", error);
    }
  };
  const handleTreat = async (item) => {
    const payload = {
      paymentId: item.id,
      treated: true
    };
    try {
      const response = await securePost(`/payments/updateTreated`, payload, showSnackbar);
      showSnackbar(t(`confirmation.treatSuccess`), 'success');
      fetchPaginatedPayments(currentPage, pageSize, filters);
    } catch (error) {
      console.error("Error treating order:", error);
    }
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "dateRangeEcheance",
      type: "daterange",
      label: "Date d'écheance",
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({
        value: client.id,
        reference: client.code,
        name: client.name
      }))],
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: isCommercial
        ? [
          {
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          },
        ]
        : commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
    },
    {
      name: "nature",
      label: t(`columns.paymentType`),
      type: "dropdown",
      options: [
        ...paymentTypes.map((type) => ({
          value: type.id,
          label: type.name,
        })),
      ],
    },
    {
      name: "paymentStatus",
      label: t(`filters.orders.status`),
      type: "checkbox-dropdown",
      options: [
        { value: "all", label: t(`placeholder.all`) },
        { value: "UNDER_APPROVAL", label: t(`payment_status.UNDER_APPROVAL`) },
      ],
    },
    {
      name: "treated",
      label: t("filters.orders.treated"),
      type: "dropdown",
      options: [
        { value: "true", label: t("treated_status.true") },
        { value: "false", label: t("treated_status.false") }
      ]
    }
  ];

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        if (!isCommercial) {
          const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
          setCommercials(commercialResponse.data);
        }

        const Payments = await secureGet("/payments/types", showSnackbar, { signal });
        setPaymentTypes(Payments.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };

    fetchDropdownOptions();

    return () => {
      abortController.abort();
    };
  }, []);


  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchPaymentsSummary = async () => {
      try {
        const response = await securePost("/payments/summary", requestBody, showSnackbar, { signal });
        setPaymentsSummary(response.data.totalAmount);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching payments summary:", error);
        }
      }
    };

    if (requestBody) {
      fetchPaymentsSummary();
    }

    return () => {
      abortController.abort();
    };
  }, [requestBody]);


  const fetchPaginatedPayments = useCallback(async (page = 0, size = pageSize, activeFilters = filters, signal) => {
    setLoading(true);

    const payload = {
      clientId: activeFilters.clientId || null,
      commercialId: activeFilters.commercialId || null,
      startDate: activeFilters.startDate || null,
      endDate: activeFilters.endDate || null,
      echeanceStartDate: activeFilters.dateRangeEcheance?.startDate || null,
      echeanceEndDate: activeFilters.dateRangeEcheance?.endDate || null,
      paymentStatus: activeFilters.paymentStatus || null,
      needle: activeFilters.needle || null,
      paymentTypeId: activeFilters.nature || null,
      treated: activeFilters.treated || null,
    };

    setRequestBody(payload);

    try {
      const response = await securePost(`/payments/paged?`, payload, showSnackbar, {
        params: { page, size },
        signal,
      });

      const { content, totalPages } = response.data;
      setPaymentsData(content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching payments:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, role]);


  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedPayments(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedPayments(0, pageSize, filters);
    }
  }, []);


  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedPayments(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedPayments(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "dateRangeEcheance") {
      updatedFilters.dateRangeEcheance = {
        startDate: filterValue?.startDate || null,
        endDate: filterValue?.endDate || null,
      };
    } else if (filterName === "paymentStatus") {
      updatedFilters.paymentStatus = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedPayments(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      paymentStatus: null,
      needle: "",
      dateRangeEcheance: undefined,
      nature: null,
      treated: null,
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedPayments(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/payments/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedPayments(currentPage, pageSize);
      showSnackbar(t(`confirmation.paymentDelete`), 'success');
    } catch (error) {
      console.error("Error deleting payment:", error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/payments/details/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };
  const handleModify = (item) => {
    history.push({
      pathname: `/payments/modify/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.payments")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.payments`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_payment`)}
            icon={AddIcon}
            onClick={() => history.push("/payments/add")}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1
            }}>
            <LoadingLogo />
          </div>
        )}
        <Rows
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => handleDetails(row)}
        />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Payments;